import { useEffect } from 'react';
import { AlertTriangle, X } from 'lucide-react';

export default function ConfirmDialog({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title, 
  description, 
  confirmText = "Confirm", 
  cancelText = "Cancel",
  isDestructive = false 
}) {
  // Handle Escape key to close
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    // Backdrop overlay
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-xs transition-all"
      onClick={onClose} // Close when clicking outside
    >
      {/* Dialog container - stop propagation to prevent closing when clicking inside */}
      <div 
        className="relative w-full max-w-md bg-card border border-border rounded-xl shadow-lg p-6 animate-in fade-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close icon button */}
        <button 
          onClick={onClose}
          className="absolute right-4 top-4 text-muted-foreground hover:text-foreground transition-colors rounded-sm opacity-70 hover:opacity-100"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
          {/* Status Icon */}
          <div className={`shrink-0 p-3 rounded-full flex items-center justify-center ${
            isDestructive ? 'bg-rose-500/10 text-rose-500' : 'bg-primary/10 text-primary'
          }`}>
            <AlertTriangle className="w-6 h-6" />
          </div>

          {/* Text Content */}
          <div className="flex-1 pt-1">
            <h2 className="text-lg font-semibold text-foreground tracking-tight">
              {title}
            </h2>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
              {description}
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium rounded-md border border-border bg-transparent hover:bg-surface-hover text-foreground transition-colors focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2"
          >
            {cancelText}
          </button>
          <button
            onClick={() => {
              onConfirm();
              onClose();
            }}
            className={`px-4 py-2 text-sm font-medium rounded-md text-white transition-colors focus:outline-hidden focus:ring-2 focus:ring-offset-2 ${
              isDestructive 
                ? 'bg-rose-500 hover:bg-rose-600 focus:ring-rose-500' 
                : 'bg-primary hover:bg-primary/90 focus:ring-primary'
            }`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}